/**
 * AppearancePopover — V2 top-bar appearance control.
 *
 * Mirrors design_reference/js/shell.jsx AppearancePop: a "Workspace brand"
 * picker, theme (dark/light) segmented, signature accent swatches, density
 * segmented, and a "Back to classic" exit. All wired to the persisted
 * `useUiStore`.
 *
 * Role gating (PRP): a carrier user (roleToBrand(roles) !== 'standard') cannot
 * switch brands — the picker is disabled. The theme segmented is disabled when
 * the active brand supports a single theme (Citizens → light only); the accent
 * swatches are disabled when the active brand locks the accent.
 */

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';
import { Segmented } from './ui/segmented';
import {
  useUiStore,
  type V2Accent,
  type V2Density,
  type V2Theme,
} from '../theme/uiStore';
import useAuthStore from '../../stores/authStore';
import { BRANDS, roleToBrand, visibleBrandsForRoles } from '../branding/registry';
import { IconCheck, IconMoon, IconSun } from './icons';
import { cn } from '../../lib/utils';

/** Accent key -> swatch fill color (matches prototype ACCENTS). */
const ACCENTS: Array<[V2Accent, string]> = [
  ['lime', '#b8ea3a'],
  ['blue', '#4f8cff'],
  ['cyan', '#2bd4e6'],
];

/**
 * Renders the appearance popover trigger (sun/moon by theme) and its content.
 *
 * @returns The appearance popover element.
 */
export function AppearancePopover() {
  const theme = useUiStore((s) => s.theme);
  const accent = useUiStore((s) => s.accent);
  const density = useUiStore((s) => s.density);
  const brand = useUiStore((s) => s.brand);
  const setTheme = useUiStore((s) => s.setTheme);
  const setAccent = useUiStore((s) => s.setAccent);
  const setDensity = useUiStore((s) => s.setDensity);
  const setBrand = useUiStore((s) => s.setBrand);
  const disableV2 = useUiStore((s) => s.disableV2);
  const roles = useAuthStore((s) => s.roles);
  const rolesFetchFailed = useAuthStore((s) => s.rolesFetchFailed);

  const activeBrand = BRANDS[brand] ?? BRANDS.standard;
  // A carrier role forces a brand → the picker is locked for that user.
  const forcedBrand = roleToBrand(roles);
  const brandLocked = forcedBrand !== 'standard';
  const themeLocked = activeBrand.supportedThemes.length < 2;
  const accentLocked = activeBrand.locksAccent;

  // Carrier skins (Citizens / State Farm) are white-label previews — only
  // super admins (or the user's own role-forced carrier) may see them.
  const visibleBrands = visibleBrandsForRoles(roles);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="icon" size="md" aria-label="Appearance">
          {theme === 'dark' ? <IconMoon /> : <IconSun />}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <p className="m-0 mb-2 text-[10px] font-semibold uppercase tracking-[.08em] text-[var(--tx-faint)]">
          Workspace brand
        </p>
        <div className="v2-brandlist">
          {visibleBrands.map((key) => {
            const def = BRANDS[key];
            const on = brand === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setBrand(key)}
                disabled={brandLocked}
                aria-pressed={on}
                className={cn('v2-brandopt', on && 'on')}
              >
                {def.label}
                {on && <IconCheck />}
              </button>
            );
          })}
        </div>
        {activeBrand.note && (
          <p className="mt-[10px] text-[10.5px] leading-[1.4] text-[var(--tx-faint)]">
            {activeBrand.note}
          </p>
        )}

        <p className="m-0 mb-2 mt-[14px] text-[10px] font-semibold uppercase tracking-[.08em] text-[var(--tx-faint)]">
          Theme
        </p>
        <div className={cn(themeLocked && 'pointer-events-none opacity-55')}>
          <Segmented<V2Theme>
            value={theme}
            onValueChange={setTheme}
            fullWidth
            options={[
              { value: 'dark', label: 'Dark', icon: <IconMoon /> },
              { value: 'light', label: 'Light', icon: <IconSun /> },
            ]}
          />
        </div>

        <p className="m-0 mb-2 mt-[14px] text-[10px] font-semibold uppercase tracking-[.08em] text-[var(--tx-faint)]">
          Signature accent
        </p>
        <div className={cn('flex gap-2', accentLocked && 'pointer-events-none opacity-55')}>
          {ACCENTS.map(([key, color]) => (
            <button
              key={key}
              type="button"
              onClick={() => setAccent(key)}
              disabled={accentLocked}
              aria-label={`${key} accent`}
              aria-pressed={accent === key}
              className={cn(
                'grid h-[34px] w-full place-items-center rounded-[var(--r-md)] border border-[var(--bd)]',
                'shadow-[inset_0_1px_0_var(--hi-top)]',
                accent === key &&
                  'shadow-[0_0_0_2px_var(--bg-2),0_0_0_4px_var(--accent)]',
              )}
              style={{
                background: `linear-gradient(180deg, color-mix(in oklab, ${color} 84%, white), ${color})`,
              }}
            >
              <span className="text-[10px] font-semibold capitalize text-black/55">
                {key}
              </span>
            </button>
          ))}
        </div>

        <p className="m-0 mb-2 mt-[14px] text-[10px] font-semibold uppercase tracking-[.08em] text-[var(--tx-faint)]">
          Density
        </p>
        <Segmented<V2Density>
          value={density}
          onValueChange={setDensity}
          fullWidth
          options={[
            { value: 'compact', label: 'Compact' },
            { value: 'regular', label: 'Regular' },
            { value: 'comfy', label: 'Comfy' },
          ]}
        />

        {/* Pay-per-conversion (role-less) users live in V2 only — the classic
            UI has no locks/credits surface for them, so hide the exit. Kept
            visible when the roles fetch FAILED (possible member). */}
        {(roles.length > 0 || rolesFetchFailed) && (
          <>
            <div className="my-3 border-t border-[var(--bd-subtle)]" />
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center"
              onClick={() => disableV2()}
            >
              ← Back to classic
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
